import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { useFreelancerContext } from "../contexts/useFreelancerContext";
import { FreelancerOnboardingStatus } from "../models/enums/FreelancerOnboardingStatus";
import Projects from "../pages/freelancer/Projects";
import ProjectDetails from "../pages/project/ProjectDetails";
import FreelancerApplication from "../pages/freelancer/FreelancerApplication";
import FreelancerOnboardingForms from "../pages/freelancer/FreelancerOnboardingForms";
import Settings from "../pages/settings/Settings";
import FreelancerActivityReports from "../pages/freelancer/FreelancerActivityReports";

/**
 * Freelancer routing rules:
 *
 * 1. If onboarding form NOT submitted (NotSubmitted):
 *    - Default: /profile (onboarding forms)
 *    - Only accessible: /profile/onboarding, /settings
 *
 * 2. If onboarding submitted but NOT accepted (Submitted to Positioning):
 *    - Default: /my-application
 *    - Only accessible: /profile/onboarding, /my-application, /settings
 *
 * 3. If ACCEPTED but evaluation NOT done (freelancerOnboardingExperienceRatingId is null):
 *    - Default: /my-application (blocked on evaluation page)
 *    - Only accessible: /my-application, /settings
 *
 * 4. If ACCEPTED and evaluation done:
 *    - Default: /projects
 *    - Full access to all routes
 */

/**
 * Inner component that uses the FreelancerContext
 */
const FreelanceRoutesContent: React.FC = () => {
	const location = useLocation();
	const { freelancerProfileQueryResult } = useFreelancerContext();
	const { data: freelancerProfile, isLoading: isFreelancerProfileLoading } =
		freelancerProfileQueryResult;

	if (isFreelancerProfileLoading || !freelancerProfile) {
		return <></>;
	}

	const currentStatus = freelancerProfile.onboarding.currentStatus;
	const hasCompletedEvaluation =
		freelancerProfile.onboarding.freelancerOnboardingExperienceRatingId &&
		freelancerProfile.onboarding.freelancerOnboardingExperienceRatingId > 0;

	// Case 1: Onboarding form NOT submitted - redirect to /profile (onboarding)
	if (
		currentStatus === FreelancerOnboardingStatus.NotSubmitted &&
		!location.state?.redirectToMyApplication
	) {
		return (
			<Routes>
				<Route path={"/profile/onboarding/*"} element={<FreelancerOnboardingForms />} />
				<Route path="/settings" element={<Settings />} />
				<Route path="*" element={<Navigate to={"/profile/onboarding"} />} />
			</Routes>
		);
	}

	// Case 2: Submitted but NOT accepted - redirect to /my-application
	if (
		currentStatus !== FreelancerOnboardingStatus.Accepted ||
		location.state?.redirectToMyApplication
	) {
		return (
			<Routes>
				<Route path={"/profile/onboarding"} element={<FreelancerOnboardingForms />} />
				<Route
					path={"/my-application"}
					element={<FreelancerApplication freelancerProfile={freelancerProfile} />}
				/>
				<Route path="/settings" element={<Settings />} />
				<Route path="*" element={<Navigate to={"/my-application"} />} />
			</Routes>
		);
	}

	// Case 3: ACCEPTED but evaluation NOT done - blocked on /my-application
	if (!hasCompletedEvaluation) {
		return (
			<Routes>
				<Route
					path={"/my-application"}
					element={<FreelancerApplication freelancerProfile={freelancerProfile} />}
				/>
				<Route path="/settings" element={<Settings />} />
				<Route path="*" element={<Navigate to={"/my-application"} />} />
			</Routes>
		);
	}

	// Case 4: ACCEPTED and evaluation done - full access, default to /projects
	return (
		<Routes>
			<Route path={"/profile"} element={<FreelancerOnboardingForms />} />
			<Route
				path={"/my-application"}
				element={<FreelancerApplication freelancerProfile={freelancerProfile} />}
			/>
			<Route path="/projects" element={<Projects />} />
			<Route path="/projects/:id" element={<ProjectDetails />} />
			<Route path="/activity-reports/:id" element={<FreelancerActivityReports />} />
			<Route path="/settings" element={<Settings />} />
			<Route path="*" element={<Navigate to="/projects" />} />
		</Routes>
	);
};

export default FreelanceRoutesContent;
